<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { mdiAlertCircleOutline, mdiRefresh } from "@mdi/js"
  import { fetchCompleteCalendarBoardProjection } from "../../../../../../../../api/kehrblatt/schedule-events"
  import type {
    CalendarBoardExternalBusyInterval,
    CalendarBoardPropertyRef,
    CalendarBoardWorkOrderRef,
  } from "../../../../../../../../api/kehrblatt/schedule-events"
  import { isCancelableAppointment } from "../../../../_shared/_logic/appointment-eligibility"
  import CalendarPageHeader from "./_modules/calendar-page-header"
  import BoardFilterBar from "./_modules/board-filter-bar"
  import { SchedulingCalendar } from "../../../../../../calendar-api"
  import { adminSession } from "../../../../../../_shared/_logic/admin-session"
  import {
    buildTimeSlots,
    formatDayHeader,
    startOfDay,
  } from "../../../../../../../../_shared/_logic/date-math"
  import { buildDayColumns, formatPropertyAddress } from "../../../../../../calendar-api"
  import { buildBulkCancelableColumnIds } from "./_logic/bulkCancelableColumns"
  import { buildWorkingHoursByUser } from "../../../../../../../../_shared/_logic/working-hours-view"
  import { filterSelectableOrganizationUsers } from "../../../../../../_shared/_logic/organization-users"
  import type { WorkingHoursByUser } from "../../../../../../../../_shared/_logic/form-state"
  import type {
    BoardActions,
    BoardColumn as BoardColumnType,
    BoardEvent,
    BoardInteractionConfig,
    BoardLayoutConfig,
    EventOverlayOptions,
    HardSlotPayload,
    SoftSlotPayload,
  } from "../../../../../../calendar-api"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { scheduleStatusLabels } from "../../../../../../../../_shared/_logic/status-definitions"
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import { resolveCalendarHourBounds } from "../../../../../../calendar-api"
  import {
    calendarDateRangeKey,
    calendarRangeExclusiveEnd,
    calendarRangeVisibleDays,
    coerceCalendarDateRangeInput,
    normalizeCalendarDateRange,
    type CalendarDateRange,
    type CalendarDateRangeInput,
  } from "./_shared/_logic/calendar-date-range"
  import {
    readCalendarUrlEmployeeIds,
    readCalendarUrlDateRange,
    writeCalendarUrlEmployeeIds,
    writeCalendarUrlDateRange,
  } from "./_logic/calendarUrlState"
  import { indexById } from "./_logic/entities"
  import {
    buildCalendarWindowBounds,
    buildCalendarWindowKey,
    projectCalendarBoardResponse,
  } from "./_logic/calendarWindow"
  import { buildCalendarRouteTransitionsFromEvents } from "../../../../../../_shared/_logic/route-transitions"
  import {
    buildEmployeeColors,
    buildEmployeeOptions,
    resolveDefaultSelectedEmployee,
    resolveTargetEmployeeIds,
  } from "./_logic/employees"
  import {
    openCreateAppointmentDialog,
    openCreateAppointmentFlow as runOpenCreateAppointmentFlow,
    openCreateBlockerFlow as runOpenCreateBlockerFlow,
  } from "./calendar-create-commands"
  import { openCalendarIntegrationManagementOverlay } from "./calendar-integration-commands"
  import { openCalendarEventReadOverlay } from "./calendar-read-commands"
  import {
    createCalendarEventUpdateQueue,
    updateCalendarEventFromBoard,
  } from "./calendar-update-commands"
  import { startCancelReplanDay as runStartCancelReplanDay } from "./calendar-bulk-commands"
  interface Props {
    organization: OrganizationFrame | null
    canManageScheduling: boolean
    allowedIntegrationEmployeeIds?: string[] | null
    allowedManageEmployeeIds?: string[] | null
  }

  let {
    organization,
    canManageScheduling,
    allowedIntegrationEmployeeIds = [],
    allowedManageEmployeeIds = [],
  }: Props = $props()
  const readInitialSelectedEmployee = () => readCalendarUrlEmployeeIds(organization?.id ?? null)
  let selectedEmployee = $state<string | string[]>(readInitialSelectedEmployee())

  const DEFAULT_DAY_START_HOUR = 6
  const DEFAULT_DAY_END_HOUR = 22
  const SLOT_MINUTES = 5
  const HOUR_HEIGHT = 202.8
  const slotHeight = HOUR_HEIGHT / (60 / SLOT_MINUTES)
  const minEventHeight = 18
  const EVENT_QUERY_LIMIT = 5000
  const eventTypeColors: Record<ScheduleEventType, string> = {
    APPOINTMENT: "var(--kontext-accent)",
    ABSENCE: "var(--primary-200)",
    INTERNAL_BLOCK: "var(--text-300)",
  }

  const statusLabels = scheduleStatusLabels

  const resolveErrorMessage = (error: unknown, fallback: string): string => {
    const message = error instanceof Error ? error.message?.trim() : ""

    return message || fallback
  }

  const planningFlagLabels: Record<ScheduleEventPlanningFlag, string> = {
    OK: "",
    TIGHT: "Eng",
    OVERFLOW: "Überlauf",
  }

  let loading = $state(true)
  let eventsLoading = $state(false)
  let error = $state<string | null>(null)
  let scheduleEvents = $state<ScheduleEvent[]>([])
  let externalBusyIntervals = $state<CalendarBoardExternalBusyInterval[]>([])
  let workOrders = $state<CalendarBoardWorkOrderRef[]>([])
  let properties = $state<CalendarBoardPropertyRef[]>([])
  let customers = $state<Customer[]>([])
  let contactPoints = $state<ContactPoint[]>([])
  let contactOrderByEventId = $state<Record<string, ContactOrder>>({})
  const eventUpdateQueue = createCalendarEventUpdateQueue()
  const readInitialCalendarDateRange = () => readCalendarUrlDateRange(organization?.id ?? null)
  let calendarDateRange = $state<CalendarDateRange>(readInitialCalendarDateRange())
  let isMobile = $state(false)
  let now = $state(new Date())
  let flexSlotMinutes = $state<number | null>(null)
  let flexSlotsPerDay = $state<number | null>(null)
  const slotMinutes = SLOT_MINUTES
  const columnWidth = 360
  const columnGap = 0
  const employees = $derived(buildEmployeeOptions({ organization, allowedEmployeeIds: null }))
  const employeeColors = $derived(buildEmployeeColors(employees))

  const activeCalendarUsers = $derived(
    filterSelectableOrganizationUsers(organization?.users ?? [], null),
  )
  const creationCalendarUsers = $derived(
    filterSelectableOrganizationUsers(organization?.users ?? [], allowedManageEmployeeIds),
  )
  const creationEmployees = $derived(
    buildEmployeeOptions({ organization, allowedEmployeeIds: allowedManageEmployeeIds }),
  )
  const activeEmployeeIds = $derived(
    activeCalendarUsers.map((employee) => employee.id).filter(Boolean),
  )
  const canCreateAppointments = $derived(Boolean(canManageScheduling && creationEmployees.length))
  const eventIsEditable = (event: ScheduleEvent): boolean =>
    Boolean(canManageScheduling && event.can_manage)
  const eventCanMoveToEmployee = (item: BoardEvent, employeeId: string | null): boolean => {
    if (!employeeId || !activeEmployeeIds.includes(employeeId)) return false

    if (item.event.work_order_id) return true

    return allowedManageEmployeeIds == null || allowedManageEmployeeIds.includes(employeeId)
  }
  const workingHoursByUser = $derived(buildWorkingHoursByUser(activeCalendarUsers))
  const calendarIntegrationEnabledEmployeeIds = $derived(
    new Set(
      scheduleEvents.flatMap((event) =>
        event.employee_id && event.calendar_sync ? [event.employee_id] : [],
      ),
    ),
  )

  const targetEmployeeIds = $derived.by(() => {
    return resolveTargetEmployeeIds({ isMobile, selectedEmployee, employees })
  })

  const ensureSelectedEmployee = () => {
    const userId = $adminSession?.user?.id
    selectedEmployee = resolveDefaultSelectedEmployee({
      currentValue: selectedEmployee,
      employees,
      userId,
      isMobile,
    })
  }

  onMount(() => {
    const matcher = window.matchMedia("(max-width: 967px)")
    const sync = (event: MediaQueryList | MediaQueryListEvent) => {
      isMobile = event.matches

      if (isMobile && Array.isArray(selectedEmployee)) {
        selectedEmployee = selectedEmployee[0] ?? ""
      } else if (!isMobile && !Array.isArray(selectedEmployee) && selectedEmployee) {
        selectedEmployee = [selectedEmployee]
      }

      ensureSelectedEmployee()
    }
    sync(matcher)
    matcher.addEventListener("change", sync)

    return () => matcher.removeEventListener("change", sync)
  })

  onMount(() => {
    now = new Date()
    const timer = window.setInterval(() => {
      now = new Date()
    }, 30000)

    return () => window.clearInterval(timer)
  })

  const normalizedCalendarDateRange = $derived(normalizeCalendarDateRange(calendarDateRange))
  const rangeStart = $derived(normalizedCalendarDateRange.start ?? startOfDay(new Date()))
  const rangeExclusiveEnd = $derived(calendarRangeExclusiveEnd(normalizedCalendarDateRange))
  const visibleDays = $derived(calendarRangeVisibleDays(normalizedCalendarDateRange))
  const visibleRangeKey = $derived(calendarDateRangeKey(normalizedCalendarDateRange))

  const workOrderById = $derived(indexById(workOrders))
  const propertyById = $derived(indexById(properties))

  const filteredEvents = $derived(
    scheduleEvents.filter((event) => {
      const start = new Date(event.start_at)
      const end = new Date(event.end_at)

      if (event.status === "CANCELED" || event.status === "NOSHOW") return false

      if (!(start < rangeExclusiveEnd && end > rangeStart)) return false

      if (!targetEmployeeIds.length) return true

      return targetEmployeeIds.includes(event.employee_id ?? "")
    }),
  )
  const cancelReplanEligibleEvents = $derived(
    filteredEvents.filter((event) => isCancelableAppointment(event) && eventIsEditable(event)),
  )
  const filteredExternalBusyIntervals = $derived(
    externalBusyIntervals.filter((interval) => {
      const start = new Date(interval.start_at)
      const end = new Date(interval.end_at)

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false

      if (!(start < rangeExclusiveEnd && end > rangeStart)) return false

      if (!targetEmployeeIds.length) return true

      return targetEmployeeIds.includes(interval.employee_id)
    }),
  )

  const calendarHourBounds = $derived(
    resolveCalendarHourBounds({
      targetEmployeeIds,
      workingHoursByUser,
      filteredEvents,
      externalBusyIntervals: filteredExternalBusyIntervals,
      fallbackDayStartHour: DEFAULT_DAY_START_HOUR,
      fallbackDayEndHour: DEFAULT_DAY_END_HOUR,
    }),
  )
  const dayStartHour = $derived(calendarHourBounds.dayStartHour)
  const dayEndHour = $derived(calendarHourBounds.dayEndHour)
  const timeSlots = $derived(buildTimeSlots(dayStartHour, dayEndHour, slotMinutes))

  const cancelReplanColumnIds = $derived(buildBulkCancelableColumnIds(cancelReplanEligibleEvents))
  const eventsByEmployee = $derived(
    filteredEvents.reduce(
      (acc, event) => {
        const key = event.employee_id ?? "__unassigned__"

        if (!acc[key]) acc[key] = []

        acc[key].push(event)

        return acc
      },
      {} as Record<string, ScheduleEvent[]>,
    ),
  )
  const externalBusyIntervalsByEmployee = $derived(
    filteredExternalBusyIntervals.reduce(
      (acc, interval) => {
        if (!acc[interval.employee_id]) acc[interval.employee_id] = []

        acc[interval.employee_id].push(interval)

        return acc
      },
      {} as Record<string, CalendarBoardExternalBusyInterval[]>,
    ),
  )
  const displayRouteTransitions = $derived(
    buildCalendarRouteTransitionsFromEvents({
      events: filteredEvents,
      workOrderById,
      propertyById,
    }),
  )

  const columns = $derived.by<BoardColumnType[]>(() =>
    buildDayColumns({
      timezone: calendarTimezone,
      targetEmployeeIds,
      visibleDays,
      eventsByEmployee,
      externalBusyIntervalsByEmployee,
      routeTransitions: displayRouteTransitions,
      employees,
      calendarSyncEnabledEmployeeIds: calendarIntegrationEnabledEmployeeIds,
      employeeColors,
      workOrderById,
      propertyById,
      contactOrderByEventId,
      workingHoursByUser,
      now,
      dayStartHour,
      dayEndHour,
      slotMinutes,
      slotHeight,
      minEventHeight,
      eventTypeColors,
      planningFlagLabels,
      statusLabels,
    }),
  )

  let calendarTimezone = $state("Europe/Berlin")
  let dataRequestId = 0
  let calendarRequestController: AbortController | null = null
  onDestroy(() => calendarRequestController?.abort())
  let hasInitialLoad = false
  let lastWindowKey: string | null = null

  const loadCalendarWindow = async ({
    force = false,
    rangeKey,
    employeeKey,
  }: { force?: boolean; rangeKey?: string; employeeKey?: string } = {}) => {
    if (!organization?.id) return

    const orgId = organization?.id

    if (!orgId) return

    const { startIso, endIso } = buildCalendarWindowBounds({ rangeStart, rangeExclusiveEnd })
    const dateRangeKey = rangeKey ?? visibleRangeKey
    const employeeIds = targetEmployeeIds
    const nextWindowKey = employeeKey
      ? `${dateRangeKey}|${employeeKey}`
      : buildCalendarWindowKey({ rangeKey: dateRangeKey, employeeIds })

    if (!force && nextWindowKey === lastWindowKey) {
      return
    }

    const requestId = ++dataRequestId
    calendarRequestController?.abort()
    const controller = new AbortController()
    calendarRequestController = controller
    error = null
    loading = loading || !scheduleEvents.length
    eventsLoading = !loading
    lastWindowKey = nextWindowKey
    try {
      const board = await fetchCompleteCalendarBoardProjection(
        { organizationId: orgId, employeeIds, startAtFrom: startIso, startAtTo: endIso },
        { signal: controller.signal, pageSize: EVENT_QUERY_LIMIT },
      )

      if (requestId !== dataRequestId) return

      calendarTimezone = board.planning_settings?.timezone || "Europe/Berlin"
      const projection = projectCalendarBoardResponse(board, SLOT_MINUTES)
      scheduleEvents = projection.events
      externalBusyIntervals = projection.externalBusyIntervals
      workOrders = projection.workOrders
      properties = projection.properties
      customers = projection.customers
      contactPoints = projection.contactPoints
      contactOrderByEventId = projection.contactOrderByEventId
      flexSlotMinutes = projection.planningSettings.flexSlotMinutes
      flexSlotsPerDay = projection.planningSettings.flexSlotsPerDay
    } catch (err) {
      if (requestId !== dataRequestId || controller.signal.aborted) return

      console.error("calendar_load_failed", err)
      error = resolveErrorMessage(err, "Kalenderdaten konnten nicht geladen werden.")
    } finally {
      if (requestId === dataRequestId) {
        loading = false
        eventsLoading = false
      }
    }
  }

  onMount(() => {
    void (async () => {
      await loadCalendarWindow({ force: true })
      hasInitialLoad = true
    })()
  })

  $effect(() => {
    // Depend on selected date range/employee selection to refetch the window
    const rangeKey = visibleRangeKey
    const employeeKey = [...targetEmployeeIds].sort().join(",")

    if (!hasInitialLoad) return

    void loadCalendarWindow({ force: false, rangeKey, employeeKey })
  })

  const refreshEvents = async () => {
    eventsLoading = true
    await loadCalendarWindow({ force: true })
  }

  const upsertEvent = (updated: ScheduleEvent) => {
    const index = scheduleEvents.findIndex((entry) => entry.id === updated.id)

    if (index >= 0) {
      const clone = [...scheduleEvents]
      clone[index] = updated
      scheduleEvents = clone
    } else {
      scheduleEvents = [...scheduleEvents, updated]
    }
  }

  const removeEvent = (eventId: string) => {
    scheduleEvents = scheduleEvents.filter((entry) => entry.id !== eventId)

    if (contactOrderByEventId[eventId]) {
      const next = { ...contactOrderByEventId }
      delete next[eventId]
      contactOrderByEventId = next
    }
  }

  const createCommandContext = () => ({
    organizationId: organization?.id ?? null,
    loading,
    eventsLoading,
    now,
    slotMinutes,
    dayEndHour,
    selectedEmployee,
    allowedEmployeeIds: allowedManageEmployeeIds,
    canManageScheduling,
    employees,
    workOrders,
    properties,
    customers,
    contactPoints,
    scheduleEvents,
    workingHoursByUser: workingHoursByUser as WorkingHoursByUser,
    targetEmployeeIds,
    flexSlotMinutes,
    flexSlotsPerDay,
    upsertEvent,
    refreshEvents,
  })

  const readCommandContext = () => ({
    organizationId: organization?.id ?? null,
    canManageScheduling,
    employees,
    workOrders,
    properties,
    customers,
    contactPoints,
    scheduleEvents,
    workingHoursByUser: workingHoursByUser as WorkingHoursByUser,
    forceReadonlyEventIds: new Set(
      scheduleEvents.filter((event) => !eventIsEditable(event)).map((event) => event.id),
    ),
    refreshEvents,
    upsertEvent,
    removeEvent,
  })

  const updateCommandContext = () => ({
    organizationId: organization?.id ?? null,
    now,
    scheduleEvents,
    workingHoursByUser: workingHoursByUser as WorkingHoursByUser,
    employees,
    workOrders,
    properties,
    upsertEvent,
    refreshEvents,
    eventUpdateQueue,
  })

  const bulkCommandContext = () => ({
    organizationId: organization?.id ?? null,
    loading,
    eventsLoading,
    allowedEmployeeIds: null,
    employees,
    workOrders,
    properties,
    customers,
    contactPoints,
    contactOrders: Object.values(contactOrderByEventId),
    refreshEvents,
  })

  const openCreateAppointmentFlow = () => {
    runOpenCreateAppointmentFlow(createCommandContext())
  }

  const openCreateBlockerFlow = (opts?: EventOverlayOptions) => {
    runOpenCreateBlockerFlow(createCommandContext(), opts)
  }

  const addHardSlot = (payload: HardSlotPayload) => {
    void openCreateAppointmentDialog(createCommandContext(), { ...payload, flexType: "HARD" })
  }

  const addSoftSlot = (payload: SoftSlotPayload) => {
    void openCreateAppointmentDialog(createCommandContext(), {
      ...payload,
      flexType: "SOFT",
      timeframeStart: payload.timeframeStart,
      timeframeEnd: payload.timeframeEnd,
    })
  }

  const addBlockerSlot = (payload: HardSlotPayload) => {
    void openCreateBlockerFlow(payload)
  }

  const openEvent = (event: ScheduleEvent) => {
    openCalendarEventReadOverlay(readCommandContext(), event)
  }

  const updateEvent = async (payload: Parameters<typeof updateCalendarEventFromBoard>[1]) => {
    if (!eventIsEditable(payload.event)) return false

    return await updateCalendarEventFromBoard(updateCommandContext(), payload)
  }

  const startCancelReplanDay = (day: Date, employeeId?: string | null) => {
    runStartCancelReplanDay(bulkCommandContext(), {
      day,
      employeeId,
      eligibleEvents: cancelReplanEligibleEvents,
    })
  }

  const openIntegrationManagement = () => {
    openCalendarIntegrationManagementOverlay({
      organization,
      allowedEmployeeIds: allowedIntegrationEmployeeIds,
      manageAllowedEmployeeIds: allowedManageEmployeeIds,
      currentUserId: $adminSession?.user?.id ?? null,
    })
  }

  const layout = $derived<BoardLayoutConfig>({
    slotHeight,
    slotMinutes,
    dayStartHour,
    minEventHeight,
    flexSlotMinutes: flexSlotMinutes ?? 60,
    flexSlotsPerDay: flexSlotsPerDay ?? 0,
  })
  const interaction = $derived<BoardInteractionConfig>({
    canDirectlyEditEvents: canManageScheduling && !isMobile,
    canMoveEvent: (item) => eventIsEditable(item.event),
    canMoveEventToEmployee: eventCanMoveToEmployee,
    canResizeEvent: (item) => eventIsEditable(item.event),
  })
  const actions = $derived<BoardActions>({
    addHardSlot,
    addSoftSlot,
    addBlockerSlot,
    openEvent,
    updateEvent,
    startCancelReplanDay,
  })

  const formatAddress = formatPropertyAddress

  const timeColumnHeight = $derived((timeSlots.length - 1) * slotHeight)

  const extractDateRangeValue = (value: unknown) => {
    if (value && typeof value === "object") {
      const detailValue = (value as { detail?: { value?: unknown } })?.detail?.value

      if (detailValue !== undefined) return detailValue

      const targetValue = (value as { target?: { value?: unknown } })?.target?.value

      if (targetValue !== undefined) return targetValue
    }

    return value
  }

  const coerceEmployeeSelection = (value: unknown): string | string[] => {
    const raw = extractDateRangeValue(value)

    if (Array.isArray(raw)) {
      return raw.map((entry) => `${entry ?? ""}`.trim()).filter(Boolean)
    }

    return `${raw ?? ""}`.trim()
  }

  const handleDateRangeChange = (value: unknown) => {
    calendarDateRange = coerceCalendarDateRangeInput(
      extractDateRangeValue(value) as CalendarDateRangeInput,
    )
    writeCalendarUrlDateRange(organization?.id ?? null, calendarDateRange)
  }

  const handleEmployeeChange = (value: unknown) => {
    selectedEmployee = coerceEmployeeSelection(value)
    writeCalendarUrlEmployeeIds(organization?.id ?? null, selectedEmployee)
  }
</script>

{#if organization}
  <SchedulingCalendar
    organizationId={organization.id}
    timezone={calendarTimezone}
    {visibleDays}
    {columns}
    {timeColumnHeight}
    {timeSlots}
    {layout}
    {actions}
    {interaction}
    {now}
    {formatDayHeader}
    {formatAddress}
    {columnGap}
    {columnWidth}
    showCancelReplan={canManageScheduling}
    {cancelReplanColumnIds}
    isLoading={loading || eventsLoading}
    stickyHeaderTop="var(--kw-page-sticky-header-top, 0px)"
    readOnly={!canManageScheduling}
    allowSlotCreation={canCreateAppointments}
    allowBlockerCreation={canManageScheduling}
    editableEmployeeIds={activeEmployeeIds}
    appointmentCreationEmployeeIds={allowedManageEmployeeIds}
    blockerCreationEmployeeIds={allowedManageEmployeeIds}
  >
    {#snippet frame(calendarControls, calendarContent)}
      <CalendarPageHeader
        {calendarControls}
        {organization}
        allowedEmployeeIds={allowedManageEmployeeIds}
        {canManageScheduling}
        createDisabled={loading || eventsLoading}
        onCreateAppointment={canCreateAppointments ? openCreateAppointmentFlow : null}
        onCreateBlocker={canManageScheduling ? openCreateBlockerFlow : null}
        onManageCalendarIntegrations={openIntegrationManagement}
        hasCalendarIntegrations={calendarIntegrationEnabledEmployeeIds.size > 0}
      />

      <div class="calendar-shell">
        <BoardFilterBar
          {calendarControls}
          {employees}
          bind:selectedEmployee
          bind:dateRange={calendarDateRange}
          eventsLoading={loading || eventsLoading}
          onDateRangeChange={handleDateRangeChange}
          onEmployeeChange={handleEmployeeChange}
          allowMulti={!isMobile}
        />

        {#if error}
          <div class="calendar-error">
            <Icon path={mdiAlertCircleOutline} size="1.1rem" />
            <span>{error}</span>
            <CTA
              type="secondaryButton"
              content="Erneut versuchen"
              onClick={() => loadCalendarWindow({ force: true })}
            >
              {#snippet iconSnippet()}
                <Icon path={mdiRefresh} />
              {/snippet}
            </CTA>
          </div>
        {:else}
          {@render calendarContent()}
        {/if}
      </div>
    {/snippet}
  </SchedulingCalendar>
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .calendar-shell {
    --cal-bg: var(--neutral-white, #ffffff);
    --cal-surface: var(--bg-300, #eceaea);
    --cal-surface-soft: #f8f6f6;
    --cal-border: rgba(0, 0, 0, 0.06);
    --cal-border-strong: rgba(0, 0, 0, 0.12);
    --cal-text: var(--text-invers-100, #2f4858);
    --cal-text-muted: var(--text-invers-150, #6d97b0);
    --cal-text-soft: var(--text-invers-200, #741e20);
    --cal-accent: var(--primary-100, #741e20);
    --cal-accent-soft: var(--primary-200, #ad514c);
    --cal-shadow: 0 18px 36px rgba(47, 72, 88, 0.12);
    .calendar-error {
      display: flex;
      align-items: center;
      gap: 0.8rem;
      padding: 0.75rem 1rem;
      background: color-mix(in srgb, var(--cal-accent-soft) 14%, transparent);
      border: 1px solid color-mix(in srgb, var(--cal-accent-soft) 35%, transparent);
      border-radius: 8px;
    }
  }
</style>
